/**
 * The flow is never free-form: nodes sit on one horizontal row and their
 * positions are DERIVED from the step order, so `[Start] → [1] → [2] → … →
 * [Publish]` always reads left to right. Dragging a node only picks a new slot
 * (see `stepIndexFromX`); the layout is recomputed and snapped afterwards.
 *
 * The pixel numbers here must stay in sync with the Tailwind size classes on
 * the node components — `w-56 h-[104px]` on an editor step node, etc. They are
 * duplicated rather than injected because React Flow needs them as numbers for
 * positioning while the DOM needs them as classes.
 */
export interface FlowLayout {
  stepWidth: number
  stepHeight: number
  gap: number
  startWidth: number
  startHeight: number
  publishWidth: number
  publishHeight: number
}

/** Editor canvas — w-56 / h-[104px] step cards. */
export const EDITOR_LAYOUT: FlowLayout = {
  stepWidth: 224,
  stepHeight: 104,
  gap: 56,
  startWidth: 104,
  startHeight: 36,
  publishWidth: 176,
  publishHeight: 72,
}

/** Read-only progress strip on the submittal detail — smaller everything. */
export const PROGRESS_LAYOUT: FlowLayout = {
  stepWidth: 184,
  stepHeight: 72,
  gap: 48,
  startWidth: 84,
  startHeight: 32,
  publishWidth: 152,
  publishHeight: 52,
}

const columnWidth = (layout: FlowLayout) => layout.stepWidth + layout.gap

const firstStepX = (layout: FlowLayout) => layout.startWidth + layout.gap

export const startPosition = (layout: FlowLayout) => ({
  x: 0,
  y: (layout.stepHeight - layout.startHeight) / 2,
})

export const stepPosition = (layout: FlowLayout, index: number) => ({
  x: firstStepX(layout) + index * columnWidth(layout),
  y: 0,
})

export const publishPosition = (layout: FlowLayout, stepCount: number) => ({
  x: firstStepX(layout) + stepCount * columnWidth(layout),
  y: (layout.stepHeight - layout.publishHeight) / 2,
})

/** Which slot a dragged step node was dropped into. */
export const stepIndexFromX = (layout: FlowLayout, x: number, stepCount: number): number => {
  const slot = Math.round((x - firstStepX(layout)) / columnWidth(layout))
  return Math.max(0, Math.min(stepCount - 1, slot))
}

/**
 * React Flow renders edges as SVG paths inside its own stylesheet-controlled
 * layer, where `.react-flow__edge .react-flow__edge-path` outranks a Tailwind
 * utility class — so the single colour the connectors need goes through React
 * Flow's own style API instead, still reading the shared border token.
 */
export const EDGE_PATH_STYLE = { stroke: 'hsl(var(--border))', strokeWidth: 1.5 }

/**
 * Keeps a dragged step node on the row — steps reorder horizontally and must
 * never wander off the line.
 *
 * React Flow clamps a node to `extent` as `clamp(y, extent[0][1], extent[1][1]
 * - nodeHeight)` (clampPosition in @xyflow/system), so the bottom corner has to
 * be a full node height BELOW the top one for the allowed range to collapse to
 * exactly y = 0. A zero-height extent would instead produce min > max and pin
 * every node at -stepHeight.
 */
export const STEP_DRAG_EXTENT: [[number, number], [number, number]] = [
  [-4000, 0],
  [40000, EDITOR_LAYOUT.stepHeight],
]
